import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Phone, Building2, Navigation, ArrowRight } from "lucide-react";
import { PageHeader, Reveal } from "@/components/public/ui";
import { Input } from "@/components/ui/input";
import { usePublic } from "@/hooks/usePublic";

const mapLink = (d) =>
  d.lat && d.lng
    ? `https://www.google.com/maps/search/?api=1&query=${d.lat},${d.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([d.address, d.city, d.state].filter(Boolean).join(", "))}`;

export default function FindDistributor() {
  const { data, loading } = usePublic("/distributors");
  const [q, setQ] = useState("");
  const list = data || [];

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((d) => [d.name, d.company, d.city, d.state, d.address].filter(Boolean).join(" ").toLowerCase().includes(s));
  }, [q, list]);

  return (
    <div data-testid="find-distributor-page">
      <PageHeader eyebrow="Find Distributor" title="Locate an Aimway distributor" subtitle="Connect with authorized Aimway distributors near you for sales, installation and support." />
      <section className="container-page py-16">
        <div className="mx-auto mb-10 max-w-xl">
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by city, state or name..." className="h-12 border-slate-200 bg-white pl-11 focus-visible:ring-brand" data-testid="distributor-search" />
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-52 animate-pulse rounded-2xl bg-slate-100" />)}</div>
        ) : filtered.length ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((d, i) => (
              <Reveal key={d.id} delay={i * 0.05}>
                <div className="flex h-full flex-col rounded-2xl border border-slate-100 bg-white p-6 shadow-card" data-testid={`distributor-${i}`}>
                  <div className="mb-4 flex items-center gap-3">
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-sky text-brand"><Building2 size={20} /></span>
                    <div>
                      <div className="font-display font-700 text-brand-navy">{d.company || d.name}</div>
                      {d.company && <div className="text-sm text-muted-foreground">{d.name}</div>}
                    </div>
                  </div>
                  <div className="flex-1 space-y-2 text-sm text-slate-600">
                    <div className="flex items-start gap-2"><MapPin size={16} className="mt-0.5 shrink-0 text-brand-orange" />{[d.address, d.city, d.state].filter(Boolean).join(", ")}</div>
                    {d.phone && <div className="flex items-center gap-2"><Phone size={16} className="text-brand-orange" /><a href={`tel:${d.phone}`} className="hover:text-brand">{d.phone}</a></div>}
                  </div>
                  <a href={mapLink(d)} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 text-sm font-700 text-brand hover:gap-3 transition-all"><Navigation size={15} /> Get directions</a>
                </div>
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-12 text-center">
            <p className="text-muted-foreground">No distributors found{q ? ` for "${q}"` : ""}. New distributors are being added regularly.</p>
          </div>
        )}

        <div className="mt-16 flex flex-col items-center gap-5 rounded-3xl bg-brand-navy p-10 text-center text-white">
          <h3 className="font-display text-2xl font-800">Want to become an Aimway distributor?</h3>
          <p className="max-w-xl text-blue-100">Partner with India's first AI helmet sanitization brand and grow with us.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-7 py-3.5 font-display font-700 text-white transition-colors hover:bg-brand-orange-dark" data-testid="become-distributor">Become a Distributor <ArrowRight size={18} /></Link>
        </div>
      </section>
    </div>
  );
}
